(() => {
  const AUTOPLAY_MS_DEFAULT = 6200;
  const JAPANESE_PATTERN = /[\u3040-\u30ff\u3400-\u9fff]/;

  const classifyTitle = (text) => {
    const length = text.replace(/\s+/g, '').length;
    if (length > 38) return 'xlong';
    if (length > 30) return 'long';
    if (length > 20) return 'medium';
    return 'short';
  };

  const addJapaneseBreakHints = (element) => {
    if (!element || element.dataset.wbrReady === 'true') return;
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);

    nodes.forEach((node) => {
      const raw = node.nodeValue;
      if (!JAPANESE_PATTERN.test(raw) || raw.trim().length < 8) return;
      const fragment = document.createDocumentFragment();
      raw.split(/([、。])/g).forEach((part) => {
        if (!part) return;
        fragment.appendChild(document.createTextNode(part));
        if (/[、。]$/.test(part)) fragment.appendChild(document.createElement('wbr'));
      });
      node.parentNode.replaceChild(fragment, node);
    });
    element.dataset.wbrReady = 'true';
  };

  const classifyCopy = (node) => {
    const title = node.querySelector('.phil-hero__title, h2, h3');
    if (!title) return;
    const rawTitle = title.textContent.trim();
    const hasJapanese = JAPANESE_PATTERN.test(rawTitle);
    const size = classifyTitle(rawTitle);
    node.dataset.copyLength = size === 'xlong' || size === 'long' ? 'long' : size === 'medium' ? 'medium' : 'short';
    node.dataset.titleSize = size;
    node.dataset.script = hasJapanese ? 'jp' : 'latin';
    if (hasJapanese) {
      addJapaneseBreakHints(title);
      addJapaneseBreakHints(node.querySelector('[data-hero-body]'));
    }
  };

  const fitHeroCopy = (copy) => {
    if (!copy) return;
    copy.classList.remove('is-compressed', 'is-tight', 'is-ultra-tight', 'is-overflowing');
    requestAnimationFrame(() => {
      if (copy.scrollHeight <= copy.clientHeight + 2) return;
      copy.classList.add('is-compressed');
      requestAnimationFrame(() => {
        if (copy.scrollHeight <= copy.clientHeight + 2) return;
        copy.classList.add('is-tight');
        requestAnimationFrame(() => {
          if (copy.scrollHeight <= copy.clientHeight + 2) return;
          copy.classList.add('is-ultra-tight');
          requestAnimationFrame(() => {
            if (copy.scrollHeight > copy.clientHeight + 2) copy.classList.add('is-overflowing');
          });
        });
      });
    });
  };

  const fitAllHeroCopies = (root) => {
    root.querySelectorAll('[data-hero-copy]').forEach(fitHeroCopy);
  };

  const initCarousel = (chamber) => {
    const slides = Array.from(chamber.querySelectorAll('[data-phil-slide]'));
    const dots = Array.from(chamber.querySelectorAll('[data-phil-dot]'));
    if (!slides.length) return;

    let index = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    let timer = null;
    let inViewport = true;
    let pausedByFocus = false;
    const delay = Number(chamber.dataset.autoplayMs || AUTOPLAY_MS_DEFAULT);

    const activate = (nextIndex) => {
      index = (Number(nextIndex) + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
      dots.forEach((dot, dotIndex) => {
        const active = dotIndex === index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });
      fitHeroCopy(slides[index].querySelector('[data-hero-copy]'));
    };

    const stop = () => {
      if (timer) window.clearTimeout(timer);
      timer = null;
    };

    const shouldPlay = () => slides.length > 1 && !document.hidden && inViewport && !pausedByFocus;

    const schedule = () => {
      stop();
      if (!shouldPlay()) return;
      timer = window.setTimeout(() => {
        activate(index + 1);
        schedule();
      }, delay);
    };

    const restart = () => {
      stop();
      schedule();
    };

    activate(index);
    restart();
    window.setTimeout(restart, 350);
    window.setTimeout(restart, 1400);

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        activate(Number(dot.dataset.philDot));
        restart();
      });
    });

    const hero = chamber.querySelector('[data-phil-hero]');
    if (hero) {
      hero.addEventListener('focusin', () => { pausedByFocus = true; stop(); });
      hero.addEventListener('focusout', () => { pausedByFocus = false; restart(); });
    }

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          inViewport = entry.isIntersecting;
          if (inViewport) restart();
          else stop();
        });
      }, { threshold: 0.05 });
      observer.observe(chamber);
    }

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stop();
      else restart();
    });
  };

  const detectTheme = () => {
    const root = document.documentElement;
    const body = document.body;
    const attrs = [
      root.dataset.theme,
      body.dataset.theme,
      root.dataset.colorScheme,
      body.dataset.colorScheme,
      root.getAttribute('data-theme'),
      body.getAttribute('data-theme'),
      root.getAttribute('data-color-scheme'),
      body.getAttribute('data-color-scheme')
    ].filter(Boolean).join(' ').toLowerCase();

    const classes = `${root.className || ''} ${body.className || ''}`.toLowerCase();
    let stored = '';
    try {
      stored = [
        localStorage.getItem('theme'),
        localStorage.getItem('color-scheme'),
        localStorage.getItem('japansolved-theme'),
        sessionStorage.getItem('theme'),
        sessionStorage.getItem('color-scheme'),
        sessionStorage.getItem('japansolved-theme')
      ].filter(Boolean).join(' ').toLowerCase();
    } catch (error) {}

    const haystack = `${attrs} ${classes} ${stored}`;
    if (/dark|night|scheme-dark|color-scheme-dark/.test(haystack)) return 'dark';
    if (/light|day|scheme-light|color-scheme-light/.test(haystack)) return 'light';
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';
    return 'light';
  };

  const syncTheme = () => {
    const theme = detectTheme();
    document.querySelectorAll('[data-theme-aware]').forEach((element) => {
      element.dataset.currentTheme = theme;
    });
  };

  const initChamber = (chamber) => {
    if (!chamber || chamber.dataset.philInitialized === 'true') return;
    chamber.dataset.philInitialized = 'true';
    chamber.querySelectorAll('[data-smart-type]').forEach(classifyCopy);
    fitAllHeroCopies(chamber);
    initCarousel(chamber);

    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(() => fitAllHeroCopies(chamber));
      observer.observe(chamber);
      chamber.querySelectorAll('[data-hero-copy]').forEach((copy) => observer.observe(copy));
    } else {
      window.addEventListener('resize', () => fitAllHeroCopies(chamber), { passive: true });
    }
  };

  const boot = (scope = document) => {
    scope.querySelectorAll('[data-phil-chamber]').forEach(initChamber);
    syncTheme();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot());
  else boot();

  document.addEventListener('shopify:section:load', (event) => boot(event.target));

  ['html', 'body'].forEach((selector) => {
    const target = document.querySelector(selector);
    if (!target) return;
    new MutationObserver(syncTheme).observe(target, {
      attributes: true,
      attributeFilter: ['data-theme', 'data-color-scheme', 'class']
    });
  });

  window.addEventListener('storage', syncTheme);
  if (window.matchMedia) {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    if (media.addEventListener) media.addEventListener('change', syncTheme);
    else if (media.addListener) media.addListener(syncTheme);
  }
})();
